import { useState, useRef, useEffect, useMemo } from "react";
import { MapElement } from "PosterEditor/utils/canvas-drawing";
import {
  MapLabel,
  StaticLabel,
  Backdrop,
  incrementSizeInPX,
  nearestIncrementValueToValue,
} from "PosterEditor/model/poster-project";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { SnapLine } from "PosterEditor/utils/rect-snapping";
import { Point, Size, Rect } from "PosterEditor/model/poster-types";
import { useEditorUI } from "PosterEditor/use-editor-ui";
import { snappingThreshold } from "PosterEditor/utils/rect-snapping";
import { config } from "process";

export interface ElementDraggingProps {}

export const useElementDragging = () => {
  const { editorUIState } = useEditorUI();
  const {
    project,
    setMapLabel,
    getStaticLabel,
    setStaticLabel,
    removeStaticLabel,
    getBackdrop,
    setBackdrop,
    removeBackdrop,
    isTemplateEditing,
  } = usePosterProject();
  const [pointerLocation, setPointerLocation] = useState<Point | undefined>(
    undefined
  );
  const [initialDraggingCenterOffset, setInitialDraggingCenterOffset] =
    useState<Point | undefined>(undefined);
  const [snapLines, setSnapLines] = useState<{
    vertical: SnapLine | undefined;
    horizontal: SnapLine | undefined;
  }>();
  const [isDragging, setIsDragging] = useState(false);
  const [draggingElement, setDraggingElement] = useState<
    MapElement | undefined
  >(undefined);
  //To reference properties of the dragging element before it was dragged.
  const [initialDraggingRect, setInitialDraggingRect] = useState<
    Rect | undefined
  >();
  const [elementUnderCursor, setElementUnderCursor] = useState<
    MapElement | undefined
  >(undefined);
  const [containerSize, setContainerSize] = useState<Size | undefined>();
  const [mapRef, setMapRef] = useState<React.MutableRefObject<any>>();

  const increment = useMemo(
    () => incrementSizeInPX(project.size),
    [project.size]
  );

  const canDragStaticLabels =
    isTemplateEditing ||
    project.editingPermissions.staticLabel.position === true;
  const canDragMapLabels =
    isTemplateEditing || project.editingPermissions.mapLabel.position === true;
  const canDragBackdrops =
    isTemplateEditing || project.editingPermissions.backdrop.position === true;

  const handleMouseDown = () => {
    if (editorUIState.editingElement?.id === elementUnderCursor?.element?.id) {
      const backdrop = elementUnderCursor?.element as Backdrop;
      if (backdrop?.size) {
        setInitialDraggingRect({
          x: backdrop.position.x,
          y: backdrop.position.y,
          width: backdrop.size.width,
          height: backdrop.size.height,
        });
      }
      if (elementUnderCursor?.type === "backdrop") {
        if (canDragBackdrops) {
          setDraggingElement(elementUnderCursor);
        }
      } else if (elementUnderCursor?.type === "staticLabel") {
        if (canDragStaticLabels) {
          setDraggingElement(elementUnderCursor);
        }
      } else {
        setDraggingElement(elementUnderCursor);
      }
    }
    if (elementUnderCursor && pointerLocation) {
      if (elementUnderCursor.type === "mapLabel" && !canDragMapLabels) {
        return;
      } else if (
        elementUnderCursor.type === "staticLabel" &&
        !canDragStaticLabels
      ) {
        return;
      } else if (elementUnderCursor.type === "backdrop" && !canDragBackdrops) {
        return;
      }

      let elementPosition: Point = {
        x: elementUnderCursor.rect.x,
        y: elementUnderCursor.rect.y,
      };
      if (elementUnderCursor.type === "staticLabel") {
        const label = elementUnderCursor.element as StaticLabel;
        if (label.style.alignment === "center") {
          elementPosition = {
            x: elementUnderCursor.rect.x + elementUnderCursor.rect.width / 2,
            y: elementUnderCursor.rect.y,
          };
        } else if (label.style.alignment === "right") {
          elementPosition = {
            x: elementUnderCursor.rect.x + elementUnderCursor.rect.width,
            y: elementUnderCursor.rect.y,
          };
        }
      }
      const centerOffset = {
        x: pointerLocation.x - elementPosition.x,
        y: pointerLocation.y - elementPosition.y,
      };

      setInitialDraggingCenterOffset(centerOffset);
      setIsDragging(true);
    } else {
      setDraggingElement(undefined);
      setIsDragging(false);
    }
  };

  const handleMouseUp = () => {
    setDraggingElement(undefined);
    setIsDragging(false);
  };

  const handleMouseMove = () => {
    if (!isDragging) {
      return;
    }
    const config: MoveConfig = {
      draggingElement,
      initialRect: initialDraggingRect,
      elementUnderCursor,
      pointerLocation,
      initialDraggingCenterOffset,
      map: mapRef,
      snapLines: snapLines,
      increment: increment,
      onSetMapLabel: setMapLabel,
      onSetStaticLabel: setStaticLabel,
      onSetBackdrop: setBackdrop,
      // containerSize,
    };
    moveWithConfig(config);
  };

  // Listen for these keypresses and move the element.
  const onRightArrowKeyboardButton = () => moveEditingElement(10, 0);
  const onLeftArrowKeyboardButton = () => moveEditingElement(-10, 0);
  const onUpArrowKeyboardButton = () => moveEditingElement(0, -10);
  const onDownArrowKeyboardButton = () => moveEditingElement(0, 10);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // switch (event.key) {
      //   case "ArrowRight":
      //     onRightArrowKeyboardButton();
      //     break;
      //   case "ArrowLeft":
      //     onLeftArrowKeyboardButton();
      //     break;
      //   case "ArrowUp":
      //     onUpArrowKeyboardButton();
      //     break;
      //   case "ArrowDown":
      //     onDownArrowKeyboardButton();
      //     break;
      //   //This isn't reliable.
      //   // case "Delete":
      //   //   deleteEditingElement();
      //   //   break;
      //   // case "Backspace":
      //   //   deleteEditingElement();
      //   //   break;
      //   default:
      //     break;
      //}
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  //Workaround for moving editing element.
  //Without this the moveEditingElement function captures only the first editingElement.
  const editingElementRef = useRef(editorUIState.editingElement);

  useEffect(() => {
    editingElementRef.current = editorUIState.editingElement;
  }, [editorUIState.editingElement]);

  const deleteEditingElement = () => {
    if (editingElementRef.current?.backdrop) {
      const id = editingElementRef.current.backdrop.id;
      if (!id) {
        return;
      }
      const backdrop = getBackdrop(id);
      if (!backdrop) {
        return;
      }
      removeBackdrop(backdrop);
    }
  };

  const moveEditingElement = (x: number, y: number) => {
    const staticLabel = editingElementRef.current?.staticLabel;
    if (staticLabel?.id) {
      const id = editingElementRef.current?.staticLabel?.id;

      if (!id) {
        return;
      }
      const label = getStaticLabel(id);
      if (!label) {
        return;
      }
      const updatedLabel = {
        ...label,
        position: {
          x: nearestIncrementValueToValue(label.position.x + x, increment),
          y: nearestIncrementValueToValue(label.position.y + y, increment),
        },
      };
      setStaticLabel(updatedLabel);
    } else if (editingElementRef.current?.backdrop) {
      const id = editingElementRef.current?.backdrop?.id;

      if (!id) {
        return;
      }
      const backdrop = getBackdrop(id);
      const mapSize = editorUIState.mapSizeInPX;

      if (!backdrop || !mapSize) {
        return;
      }
      const origin: Point = {
        x: backdrop.position.x * mapSize.width,
        y: backdrop.position.y * mapSize.height,
      };
      const newPosition: Point = {
        x: nearestIncrementValueToValue(origin.x + x, increment),
        y: nearestIncrementValueToValue(origin.y + y, increment),
      };
      const relativePosition: Point = {
        x: newPosition.x / mapSize.width,
        y: newPosition.y / mapSize.height,
      };
      const updatedBackdrop = {
        ...backdrop,
        position: relativePosition,
      };
      setBackdrop(updatedBackdrop);
    }
  };

  return {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    setPointerLocation,
    pointerLocation,
    isDragging,
    setDraggingElement,
    elementUnderCursor,
    setElementUnderCursor,
    setSnapLines,
    setContainerSize,
    setMapRef,
  };
};

interface MoveConfig {
  draggingElement: MapElement | undefined;
  initialRect: Rect | undefined;
  elementUnderCursor: MapElement | undefined;
  pointerLocation: { x: number; y: number } | undefined;
  initialDraggingCenterOffset: { x: number; y: number } | undefined;
  map: React.MutableRefObject<any> | undefined;
  // containerSize: Size;
  snapLines:
    | {
        vertical: SnapLine | undefined;
        horizontal: SnapLine | undefined;
      }
    | undefined;
  increment: number;
  onSetMapLabel: (label: MapLabel) => void;
  onSetStaticLabel: (label: StaticLabel) => void;
  onSetBackdrop: (backdrop: Backdrop) => void;
}

const moveWithConfig = (config: MoveConfig) => {
  if (
    config.map &&
    config.draggingElement &&
    (config.draggingElement.type === "mapLabel" ||
      config.draggingElement.type === "staticLabel" ||
      config.draggingElement.type === "backdrop") &&
    config.pointerLocation
  ) {
    const position = config.pointerLocation;
    const centerOffset = {
      x: config.initialDraggingCenterOffset?.x ?? 0,
      y: config.initialDraggingCenterOffset?.y ?? 0,
    };

    const mapWidth = config.map.current.getContainer().clientWidth;
    const mapHeight = config.map.current.getContainer().clientHeight;

    if (config.draggingElement.type === "mapLabel") {
      let label = config.draggingElement.element as MapLabel;
      const labelCoordinatePoint = config.map.current.project([
        label.coordinate.lng,
        label.coordinate.lat,
      ]);
      const newLabelOffsetFromCoordinate = {
        x:
          position.x -
          centerOffset.x -
          labelCoordinatePoint.x +
          (label.style.padding ?? 0),
        y:
          position.y -
          centerOffset.y -
          labelCoordinatePoint.y +
          (label.style.padding ?? 0),
      };
      let style = label.style;
      style.offset = newLabelOffsetFromCoordinate;
      label.style = style;
      config.onSetMapLabel(label);
    } else if (config.draggingElement.type === "staticLabel") {
      let label = config.draggingElement.element as StaticLabel;

      let xFromCursor = position.x - centerOffset.x;
      let yFromCursor = position.y - centerOffset.y;

      let x = xFromCursor;
      let y = yFromCursor;

      const rect = config.elementUnderCursor?.rect;
      if (rect) {
        //Depending on the label alignment we want to snap to the grid
        //using either the origin, center, or end of the label in the X axis.

        let anchorOffsetX = 0;
        let anchorOffsetY = 0;
        //We need to get the 'anchor' or the point of the label we want to snap to the grid.
        //Then snap than to the nearest increment, and then calculate the new position of the label.
        if (label.style.alignment === "left") {
          anchorOffsetX = -label.style.padding;
          anchorOffsetY = -label.style.padding;
        } else if (label.style.alignment === "right") {
          anchorOffsetX = label.style.padding;
        }
        const newAnchorX = x + anchorOffsetX;
        const newAnchorY = y + anchorOffsetY;

        const newCenterX = newAnchorX - anchorOffsetX;
        const newCenterY = newAnchorY - anchorOffsetY;
        x = newCenterX;
        y = newCenterY;
      }

      const relativeOrigin = {
        x: nearestIncrementValueToValue(x, config.increment) / mapWidth,
        y: nearestIncrementValueToValue(y, config.increment) / mapHeight,
      };

      label.position = relativeOrigin;
      config.onSetStaticLabel(label);
    } else if (config.draggingElement.type === "backdrop") {
      let backdrop = config.draggingElement.element as Backdrop;

      let xFromCursor = position.x - centerOffset.x;
      let yFromCursor = position.y - centerOffset.y;

      const dragHandle = config.draggingElement.dragHandleUnderCursor;
      if (!dragHandle) {
        //We're just dragging the whole backdrop.
        let x = xFromCursor;
        let y = yFromCursor;

        const relativeOrigin = {
          x: nearestIncrementValueToValue(x, config.increment) / mapWidth,
          y: nearestIncrementValueToValue(y, config.increment) / mapHeight,
        };

        backdrop.position = relativeOrigin;
        config.onSetBackdrop(backdrop);
      }
      if (dragHandle) {
        //Move the top left or bottom right corner of the backdrop.
        const relativeRect = config.initialRect!;
        const actualRect: Rect = {
          x: relativeRect.x * mapWidth,
          y: relativeRect.y * mapHeight,
          width: relativeRect.width * mapWidth,
          height: relativeRect.height * relativeRect.width * mapWidth,
        };
        const diff: Point = {
          x: actualRect.x - xFromCursor,
          y: actualRect.y - yFromCursor,
        };

        const initialRect = config.initialRect;
        if (dragHandle.position === "leading" && initialRect) {
          const result: Rect = {
            x: nearestIncrementValueToValue(
              actualRect.x - diff.x,
              config.increment
            ),
            y: nearestIncrementValueToValue(
              actualRect.y - diff.y,
              config.increment
            ),
            width: Math.max(
              0.1,
              nearestIncrementValueToValue(
                actualRect.width + diff.x,
                config.increment
              )
            ),
            height: nearestIncrementValueToValue(
              actualRect.height + diff.y,
              config.increment
            ),
          };
          const relativeResult: Rect = {
            x: result.x / mapWidth,
            y: result.y / mapHeight,
            width: result.width / mapWidth,
            height: result.height / result.width,
          };
          backdrop.position = {
            x: relativeResult.x,
            y: relativeResult.y,
          };
          backdrop.size = {
            width: relativeResult.width,
            height: relativeResult.height,
          };
        } else if (dragHandle.position === "trailing") {
          const result: Size = {
            width: nearestIncrementValueToValue(
              actualRect.width - diff.x,
              config.increment
            ),
            height: nearestIncrementValueToValue(
              actualRect.height - diff.y,
              config.increment
            ),
          };
          const relativeResult: Size = {
            width: result.width / mapWidth,
            height: result.height / result.width,
          };

          // Map backdrops height relative to their width.
          // They mostly contain labels, which are entirely relative to map width.
          // so if a map size is much narrower than before, the font size will reduce, our backdrop
          // width will reduce, but the heigth will be the same, so the overall aspect ratio has changed.

          const heightRatio = relativeResult.height / relativeResult.width;
          backdrop.size = {
            width: relativeResult.width,
            height: relativeResult.height,
          };
        }
        config.onSetBackdrop(backdrop);
        return;
      }
    }
  }
};
